import React, { useState } from 'react';
import styled from 'styled-components';
import { AthleteFormData } from '../../types/mongodb';

interface AthleteFormProps {
  onClose: () => void;
  onSubmit: (athlete: AthleteFormData) => void;
  hideAthleteId?: boolean;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 3rem;
  animation: fadeIn 0.3s ease;

  @media (max-width: 768px) {
    padding: 1rem;
    align-items: center;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: var(--bg-sidebar);
  padding: 2rem 2rem 1.75rem;
  border-radius: 1rem;
  width: 90%;
  max-width: 440px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
    max-width: none;
    padding: 1.5rem 1.25rem 1.5rem;
    max-height: 90vh;
    overflow-y: auto;
  }

  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  h2 {
    font-size: 1.25rem;
    margin: 0 0 1.25rem 0;
    font-family: 'Poppins', sans-serif;
    color: var(--accent-orange);
    font-weight: 900;
    text-align: left;
    letter-spacing: -0.02em;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  z-index: 1;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  position: relative;
`;

const Label = styled.label`
  font-weight: 700;
  color: var(--text-gray);
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  margin-bottom: 0.1rem;
  font-family: 'Poppins', sans-serif;
`;

const Input = styled.input.attrs({
  className: 'athlete-form-input'
})`
  padding: 0.6rem 0.9rem;
  background: #fff !important;
  border: 1px solid var(--card-border, #e0e6ed) !important;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  transition: border 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-orange) !important;
    box-shadow: 0 0 0 2px rgba(255, 150, 0, 0.15);
  }

  &::placeholder {
    color: var(--text-muted) !important;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background: #e8e8e8 !important;
  }

  &[type="date"] {
    background: #fff !important;
    color: var(--text-gray) !important;

    &:focus {
      background: #fff !important;
    }

    &::-webkit-calendar-picker-indicator {
      filter: none;
      cursor: pointer;
      opacity: 0.6;
    }
  }
`;

const FieldError = styled.div`
  font-size: 0.8rem;
  color: #c62828;
  margin-top: 0.25rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.25rem;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: stretch;
    gap: 0.75rem;
    margin-top: 1.25rem;
    flex-wrap: wrap;
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.6rem 1.25rem;
  border-radius: 0.5rem;
  border: 2px solid ${props => props.$variant === 'primary' ? 'var(--accent-orange)' : 'var(--accent-orange)'};
  background: ${props => props.$variant === 'primary' ? 'var(--accent-orange)' : 'transparent'};
  color: ${props => props.$variant === 'primary' ? '#fff' : 'var(--accent-orange)'};
  font-family: 'Poppins', sans-serif;
  font-size: 0.75rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 120px;
  min-height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  position: relative;
  white-space: nowrap;
  box-shadow: ${props => props.$variant === 'primary' ? '0 2px 8px rgba(255, 150, 0, 0.3)' : 'none'};

  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
    padding: 0.6rem 1rem;
    min-height: 2.75rem;
  }

  &:hover {
    background: ${props => props.$variant === 'primary' ? 'var(--accent-orange-dark)' : 'var(--accent-orange)'};
    color: #fff;
    border-color: ${props => props.$variant === 'primary' ? 'var(--accent-orange-dark)' : 'var(--accent-orange)'};
    box-shadow: ${props => props.$variant === 'primary' ? '0 4px 12px rgba(255, 150, 0, 0.35)' : 'none'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  span {
    position: relative;
    z-index: 1;
  }
`;

const Select = styled.select`
  padding: 0.6rem 0.9rem;
  background: #fff !important;
  border: 1px solid var(--card-border, #e0e6ed) !important;
  border-radius: 0.5rem;
  font-size: 0.9rem;
  color: var(--text-gray) !important;
  font-family: 'Poppins', sans-serif;
  transition: border 0.2s, box-shadow 0.2s;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
  min-height: 2.5rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--accent-orange) !important;
    box-shadow: 0 0 0 2px rgba(255, 150, 0, 0.15);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background: #e8e8e8 !important;
  }
`;

const MIN_AGE = 16;

function getAgeFromDateOfBirth(dateOfBirth: string): number | null {
  if (!dateOfBirth || !dateOfBirth.trim()) return null;
  const d = new Date(dateOfBirth.trim());
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const monthDiff = today.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d.getDate())) age -= 1;
  return age;
}

const AthleteForm: React.FC<AthleteFormProps> = ({ onClose, onSubmit, hideAthleteId = false }) => {
  const [formData, setFormData] = useState<AthleteFormData>({
    firstName: '',
    lastName: '',
    club: '',
    dateOfBirth: '',
    gender: undefined
  });
  const [dateOfBirthError, setDateOfBirthError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'dateOfBirth') setDateOfBirthError(null);

    // For firstName and lastName, only allow letters, spaces, hyphens, and apostrophes
    if (name === 'firstName' || name === 'lastName') {
      const textOnlyRegex = /^[a-zA-ZÀ-ÿ\s'-]*$/;
      if (!textOnlyRegex.test(value)) {
        return;
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateDateOfBirth = (): boolean => {
    if (!formData.dateOfBirth || !formData.dateOfBirth.trim()) {
      setDateOfBirthError(null);
      return true;
    }
    const age = getAgeFromDateOfBirth(formData.dateOfBirth);
    if (age === null) {
      setDateOfBirthError('Please enter a valid date of birth.');
      return false;
    }
    if (age < MIN_AGE) {
      setDateOfBirthError('Athlete must be at least 16 years old.');
      return false;
    }
    setDateOfBirthError(null);
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateDateOfBirth()) return;
    onSubmit(formData);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <h2>Add New Athlete</h2>
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="firstName">First Name</Label>
            <Input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="club">Ski Jumping Club</Label>
            <Input
              type="text"
              id="club"
              name="club"
              value={formData.club}
              onChange={handleChange}
              placeholder="Enter club name (optional)"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="dateOfBirth">Date of Birth (optional)</Label>
            <Input
              type="date"
              id="dateOfBirth"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleChange}
              onBlur={() => formData.dateOfBirth?.trim() && validateDateOfBirth()}
              aria-invalid={!!dateOfBirthError}
              aria-describedby={dateOfBirthError ? 'dateOfBirth-error' : undefined}
            />
            {dateOfBirthError && (
              <FieldError id="dateOfBirth-error" role="alert">
                {dateOfBirthError}
              </FieldError>
            )}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="gender">Gender</Label>
            <Select
              id="gender"
              name="gender"
              value={formData.gender || ''}
              onChange={handleChange}
            >
              <option value="">Select gender (optional)</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
            </Select>
          </FormGroup>

          <ButtonGroup>
            <Button type="button" onClick={onClose}>
              <span>Cancel</span>
            </Button>
            <Button type="submit" $variant="primary">
              <span>Add Athlete</span>
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AthleteForm;
